import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { NAVY, RADIUS_CARD, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import type { CampaignRow } from '../types';

const labelSx = {
  color: alpha(NAVY, 0.55),
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  fontSize: '0.62rem',
  fontWeight: 600,
  fontFamily: 'Jost, sans-serif',
};

const valueSx = {
  color: NAVY,
  fontFamily: 'Jost, sans-serif',
  fontWeight: 700,
  fontSize: '1.15rem',
  lineHeight: 1.1,
};

const hintSx = {
  color: alpha(NAVY, 0.55),
  fontFamily: 'Jost, sans-serif',
  fontSize: '0.65rem',
  fontWeight: 500,
};

interface KpiCardProps {
  label: string;
  value: string;
  hint?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, hint }) => (
  <Box
    sx={{
      px: 1.5,
      py: 1.25,
      borderRadius: RADIUS_CARD,
      bgcolor: SURFACE_CONTAINER_LOWEST,
      boxShadow: SHADOW,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.25,
      minHeight: 68,
    }}
  >
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={valueSx}>{value}</Typography>
    {hint && <Typography sx={hintSx}>{hint}</Typography>}
  </Box>
);

interface KpiStripProps {
  rows: CampaignRow[];
}

const sumNullable = (values: (number | null)[]): number => values.reduce<number>((acc, v) => acc + (v ?? 0), 0);

const firstNonNullCurrency = (rows: CampaignRow[]): string => {
  const match = rows.find((r) => Boolean(r.currency));
  return match?.currency ?? 'CLP';
};

const formatCurrency = (value: number, currency: string): string => {
  const prefix = currency ? `${currency}$ ` : '$ ';
  return `${prefix}${value.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
};

const formatNumber = (value: number): string => value.toLocaleString('es-CL', { maximumFractionDigits: 0 });

const formatPct = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
};

const KpiStrip: React.FC<KpiStripProps> = ({ rows }) => {
  const currency = firstNonNullCurrency(rows);
  const totalSpend = sumNullable(rows.map((r) => r.spend));
  const totalLeads = rows.reduce((acc, r) => acc + r.totalLeads, 0);
  const totalContacted = rows.reduce((acc, r) => acc + r.leadsContacted, 0);
  const totalCrm = rows.reduce((acc, r) => acc + r.leadsInCrm, 0);
  const crmRate = totalLeads > 0 ? totalCrm / totalLeads : null;
  const cplCrm = totalSpend > 0 && totalCrm > 0 ? totalSpend / totalCrm : null;
  const activeCampaigns = rows.filter((r) => r.status === 'active').length;
  const totalCampaigns = rows.length;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          lg: 'repeat(7, 1fr)',
        },
        gap: 1.5,
      }}
    >
      <KpiCard label="Gasto total" value={formatCurrency(totalSpend, currency)} hint="Solo Meta" />
      <KpiCard label="Leads" value={formatNumber(totalLeads)} hint={`${totalCampaigns} campañas`} />
      <KpiCard label="Contactados" value={formatNumber(totalContacted)} />
      <KpiCard label="En CRM" value={formatNumber(totalCrm)} />
      <KpiCard label="Tasa CRM" value={formatPct(crmRate)} />
      <KpiCard
        label="CPL CRM"
        value={cplCrm !== null ? formatCurrency(cplCrm, currency) : '—'}
        hint="Spend / Leads CRM"
      />
      <KpiCard label="Campañas activas" value={formatNumber(activeCampaigns)} hint={`de ${totalCampaigns}`} />
    </Box>
  );
};

export default KpiStrip;
